import classNames from "classnames";
import { Immutable } from "immer";
import React from "react";
import { msgid, ngettext, t } from "ttag";

import {
    cardImage,
    cardValueContent,
    derivedRows,
    optionDisplayName,
    rankedCardVariants,
} from "../../apps/configurator/cards";
import { cardDrawerSelector } from "../../apps/configurator/selectors";
import { IOptionCode, IOptionSingleValue } from "../../models/catalogue";
import { formatNumber } from "../../utils/format";
import { ConfiguratorProductPrice } from "../ConfiguratorProductPrice";
import { EditorialBadgeChip } from "../EditorialBadgeChip";
import { Image } from "../Image";
import { RatingGraphic } from "../RatingGraphic";

import styles from "./index.module.scss";

type ICardDrawer = NonNullable<ReturnType<typeof cardDrawerSelector>>;

type IProps = Immutable<{
    drawer: ICardDrawer;
    selectorID: string;
    legend: string;
    code: IOptionCode;
    value?: IOptionSingleValue;
    options: IOptionSingleValue[];
    onChange: (event: React.FormEvent<HTMLInputElement>) => void;
}>;

const badgeStyleClass = (style: string | undefined): string | undefined => {
    switch (style) {
        case "light":
            return styles.badgeLight;
        case "primary":
            return styles.badgePrimary;
        case "accent":
            return styles.badgeAccent;
        default:
            return undefined;
    }
};

const ProductOptionCard = (
    props: Immutable<{
        drawer: ICardDrawer;
        selectorID: string;
        code: IOptionCode;
        value: IOptionSingleValue;
        selected: boolean;
        isTabStop: boolean;
        onChange: (event: React.FormEvent<HTMLInputElement>) => void;
    }>,
): React.JSX.Element | null => {
    const { drawer } = props;
    const variants = rankedCardVariants(
        drawer.root,
        drawer.orderedCodes,
        drawer.selections,
        props.code,
        props.value,
        drawer.displayOrder,
    );
    const representative = variants.at(0);
    if (!representative) {
        return null;
    }
    const image = cardImage(drawer.root, variants);
    const content = cardValueContent(
        drawer.rootContent,
        props.code,
        props.value,
    );
    const rows = [
        ...derivedRows(
            drawer.root,
            drawer.orderedCodes,
            drawer.selections,
            props.code,
            props.value,
            drawer.displayOrder,
        ).map((row) => ({
            label: row.label,
            copy: row.values
                .map((v) => optionDisplayName(drawer.root, row.code, v))
                .join(", "),
        })),
        ...(content?.rows ?? []),
    ];
    const rating = drawer.rootContent?.rating ?? null;
    const inputID = `${props.selectorID}-${props.value}`;
    const name = optionDisplayName(drawer.root, props.code, props.value);
    const reviewCount = rating ? formatNumber(rating.num_reviews) : "";

    return (
        <li className={styles.item}>
            <div className={styles.heading}>
                <span id={`${inputID}-name`} className={styles.name}>
                    {name}
                </span>
                {rating && (
                    <div className={styles.rating}>
                        <RatingGraphic
                            rating={rating.rating}
                            numReviews={rating.num_reviews}
                            onlyStars={true}
                            cardClass={"product-option-card"}
                            cardSize={"small"}
                        />
                        <span className={styles.reviewCount} aria-hidden="true">
                            ({reviewCount})
                        </span>
                        <span className="ada-screenreader-only">
                            {ngettext(
                                msgid`${reviewCount} review`,
                                `${reviewCount} reviews`,
                                rating.num_reviews,
                            )}
                        </span>
                    </div>
                )}
            </div>
            <div
                className={classNames(styles.card, {
                    [styles.selected]: props.selected,
                })}
            >
                <input
                    type="radio"
                    className={styles.input}
                    id={inputID}
                    name={props.selectorID}
                    value={props.value}
                    checked={props.selected}
                    tabIndex={props.isTabStop ? 0 : -1}
                    aria-labelledby={`${inputID}-name ${inputID}-body`}
                    onChange={props.onChange}
                />
                <label
                    htmlFor={inputID}
                    id={`${inputID}-body`}
                    className={styles.body}
                >
                    {content?.badge && (
                        <span className="ada-screenreader-only">
                            {content.badge.display_label}
                        </span>
                    )}
                    <span className={styles.top}>
                        <span
                            className={classNames(styles.pricing, {
                                [styles.pricingWithBadge]: !!content?.badge,
                            })}
                        >
                            <span className={styles.startingAt}>
                                {t`Starting at`}
                            </span>
                            <ConfiguratorProductPrice
                                price={representative.price}
                                strikeThroughMSRP={true}
                                actualPriceStyle={""}
                                discountDisplayMode={
                                    drawer.settings.discountDisplayMode
                                }
                                percentageDiscountFormat={
                                    drawer.settings.percentageDiscountFormat
                                }
                                absoluteDiscountFormat={
                                    drawer.settings.absoluteDiscountFormat
                                }
                                retailPriceClass={styles.retailPrice}
                                actualPriceClass={styles.actualPrice}
                            />
                        </span>
                        {image && (
                            <Image
                                className={styles.image}
                                src={image.original}
                                alt=""
                            />
                        )}
                    </span>
                    {rows.length > 0 && (
                        <span className={styles.rows}>
                            {rows.map((row, i) => (
                                <span key={i} className={styles.row}>
                                    {row.label && (
                                        <span className={styles.rowLabel}>
                                            {`${row.label}: `}
                                        </span>
                                    )}
                                    {row.copy}
                                </span>
                            ))}
                        </span>
                    )}
                </label>
                {/* Sits over the label; clicks pass through to it (see .badge). */}
                {content?.badge && (
                    <span aria-hidden="true">
                        <EditorialBadgeChip
                            className={classNames(
                                styles.badge,
                                badgeStyleClass(content.badge.style),
                            )}
                            badge={{
                                display_label: content.badge.display_label,
                                // The card design has no badge tooltip.
                                description: "",
                            }}
                        />
                    </span>
                )}
            </div>
        </li>
    );
};

/**
 * Renders an option drawer's values as merchandised product cards (Product
 * Card Drawers spec), for a drawer cardDrawerSelector returned.
 */
export const ProductOptionCardSelector = (props: IProps): React.ReactNode => {
    // One tab stop for the group, as in the pill drawer.
    const tabStop =
        props.value !== undefined && props.options.includes(props.value)
            ? props.value
            : props.options.at(0);
    return (
        <fieldset className={styles.fieldset}>
            <legend className="ada-screenreader-only">{props.legend}</legend>
            <ul className={styles.cards}>
                {props.options.map((value) => (
                    <ProductOptionCard
                        key={value}
                        drawer={props.drawer}
                        selectorID={props.selectorID}
                        code={props.code}
                        value={value}
                        selected={value === props.value}
                        isTabStop={value === tabStop}
                        onChange={props.onChange}
                    />
                ))}
            </ul>
        </fieldset>
    );
};
